import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  toApiError,
  useApi,
  type ApiError,
  type PropertyPortalBrandingResponse,
} from '@/shared/api';

export function usePortalBranding(groupId: string) {
  const api = useApi();
  const client = useQueryClient();
  const queryKey = ['property-groups', groupId, 'guest-experience', 'branding'];
  const query = useQuery({
    queryKey,
    queryFn: () => api.propertyGroups.guestExperience.branding.get(groupId),
  });
  const [brandName, setBrandName] = useState('');
  const [dirty, setDirty] = useState(false);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [reloadFailed, setReloadFailed] = useState(false);

  useEffect(() => {
    setBrandName('');
    setDirty(false);
    setProblem(null);
    setReloadFailed(false);
  }, [groupId]);
  useEffect(() => {
    if (query.data && !dirty) setBrandName(query.data.brandName ?? '');
  }, [dirty, query.data]);

  const publish = (saved: PropertyPortalBrandingResponse) => {
    client.setQueryData(queryKey, saved);
  };
  const save = useMutation({
    mutationFn: () =>
      api.propertyGroups.guestExperience.branding.update(groupId, {
        expectedConcurrency: query.data?.concurrency,
        brandName: brandName.trim() || null,
      }),
    // Offline pausiert TanStack sonst still, und der Knopf dreht sich ohne Ende.
    networkMode: 'always',
    onMutate: () => {
      setProblem(null);
      setReloadFailed(false);
    },
    onSuccess: (saved) => {
      publish(saved);
      setBrandName(saved.brandName ?? '');
      setDirty(false);
      setProblem(null);
      setReloadFailed(false);
      toast.success('Portal-Branding gespeichert');
    },
    onError: (error) => {
      void toApiError(error).then(setProblem);
      toast.error('Portal-Branding konnte nicht gespeichert werden.');
    },
  });
  const upload = useMutation({
    mutationFn: (file: File) =>
      api.propertyGroups.guestExperience.uploadLogo(groupId, file, query.data?.concurrency),
    // Offline pausiert TanStack sonst still, und der Knopf dreht sich ohne Ende.
    networkMode: 'always',
    onMutate: () => {
      setProblem(null);
      setReloadFailed(false);
    },
    onSuccess: (saved) => {
      publish(saved);
      setProblem(null);
      setReloadFailed(false);
      toast.success('Portal-Logo gespeichert');
    },
    onError: (error) => {
      void toApiError(error).then(setProblem);
      toast.error('Logo konnte nicht verarbeitet werden.');
    },
  });
  const remove = useMutation({
    mutationFn: () =>
      api.propertyGroups.guestExperience.removeLogo(groupId, query.data?.concurrency),
    // Offline pausiert TanStack sonst still, und der Knopf dreht sich ohne Ende.
    networkMode: 'always',
    onMutate: () => {
      setProblem(null);
      setReloadFailed(false);
    },
    onSuccess: (saved) => {
      publish(saved);
      setProblem(null);
      setReloadFailed(false);
      toast.success('Portal-Logo entfernt');
    },
    onError: (error) => {
      void toApiError(error).then(setProblem);
      toast.error('Portal-Logo konnte nicht entfernt werden.');
    },
  });

  return {
    query,
    brandName,
    dirty,
    problem,
    reloadFailed,
    save,
    upload,
    remove,
    changeBrandName: (value: string) => {
      setBrandName(value);
      setDirty(value.trim() !== (query.data?.brandName ?? ''));
    },
    reload: async () => {
      const fresh = await query.refetch();
      if (fresh.isSuccess && fresh.data) {
        setBrandName(fresh.data.brandName ?? '');
        setDirty(false);
        setProblem(null);
        setReloadFailed(false);
        return;
      }
      const nextProblem = await toApiError(fresh.error);
      setProblem({
        ...nextProblem,
        detail: 'Aktueller Stand konnte nicht geladen werden. Ihre Änderungen bleiben erhalten.',
      });
      setReloadFailed(true);
    },
  };
}
